/**
 * AssetSilhouette — SVG side-profile silhouettes for all 29 asset types.
 *
 * Inline SVG paths ensure consistent styling, zero external deps,
 * and CSS-controllable fill colors on the terminal dark theme.
 */

interface AssetSilhouetteProps {
  assetType: string
  className?: string
  width?: number
  height?: number
  color?: string
}

export function AssetSilhouette({
  assetType,
  className = '',
  width = 120,
  height = 48,
  color = 'currentColor',
}: AssetSilhouetteProps) {
  const paths = SILHOUETTE_PATHS[assetType] ?? SILHOUETTE_PATHS._default
  return (
    <svg
      viewBox="0 0 120 48"
      width={width}
      height={height}
      className={className}
      fill={color}
      xmlns="http://www.w3.org/2000/svg"
    >
      {paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </svg>
  )
}

/**
 * Map of asset type name → SVG path data arrays.
 * All paths drawn in a 120×48 viewBox, side-profile facing right.
 */
const SILHOUETTE_PATHS: Record<string, string[]> = {
  // ===== AIR — FIGHTERS =====
  'F-22A Raptor': [
    // Stealthy angular fuselage, twin canted tails, trapezoidal wings
    'M8 28 L22 26 L30 18 L48 16 L62 14 L80 12 L95 10 L108 12 L112 14 L110 18 L105 20 L108 10 L112 8 L114 12 L110 20 L100 22 L80 24 L60 26 L40 28 L30 30 L20 30 Z',
    // Wing
    'M42 26 L32 38 L28 40 L36 38 L58 28',
    // Tail fins
    'M95 12 L100 4 L104 4 L102 12',
    'M100 14 L106 6 L110 6 L106 14',
  ],
  'F-16C/D Viper': [
    // Slim fuselage, single engine, delta wing, single tail
    'M6 26 L18 24 L28 22 L42 20 L60 18 L80 16 L92 14 L102 12 L110 14 L112 16 L108 18 L100 20 L80 22 L60 24 L40 26 L28 28 L18 28 Z',
    // Wing
    'M44 24 L30 38 L26 40 L34 38 L56 26',
    // Tail
    'M96 14 L100 4 L106 4 L104 14',
    // Intake
    'M22 24 L18 28 L24 28 L26 24',
  ],
  'F-15E Strike Eagle': [
    // Twin-engine, large wing, twin tails, intake ramps
    'M6 26 L18 24 L26 20 L40 18 L60 16 L80 14 L95 12 L106 10 L112 12 L114 16 L110 18 L100 20 L80 22 L60 24 L40 26 L28 28 L18 28 Z',
    // Wings (larger than F-16)
    'M40 24 L26 40 L22 42 L32 40 L58 26',
    // Twin tails
    'M94 12 L96 2 L100 2 L100 12',
    'M100 12 L104 2 L108 2 L106 12',
    // Intake
    'M22 24 L18 28 L26 28 L28 22',
  ],
  // ===== AIR — BOMBERS =====
  'B-2A Spirit': [
    // Flying wing — no fuselage, pure cranked-kite planform
    'M10 26 L30 24 L50 22 L70 20 L90 22 L110 26 L90 28 L80 26 L70 24 L60 24 L50 24 L40 26 L30 28 Z',
    // Jagged trailing edge (stealth serrations)
    'M30 28 L36 30 L42 28 L48 30 L54 28 L60 30 L66 28 L72 30 L78 28 L84 30 L90 28',
    // Cockpit hump
    'M52 22 L56 20 L64 20 L68 22',
  ],
  'B-1B Bone': [
    // Blended wing body, variable-sweep wings (shown swept), 4 engines, small tail
    'M4 26 L16 24 L30 22 L50 18 L70 16 L90 14 L104 12 L112 14 L114 16 L110 18 L100 20 L80 22 L60 24 L40 26 L28 30 L16 30 Z',
    // Swept wings
    'M48 22 L32 36 L28 38 L38 36 L62 24',
    // Tail
    'M100 14 L104 6 L110 6 L108 14',
    // Engine nacelles
    'M56 24 L54 28 L58 28 L58 24',
    'M64 22 L62 26 L66 26 L66 22',
  ],
  // ===== AIR — C2/ISR =====
  'E-3 AWACS': [
    // 707-based airframe with distinctive rotodome on pylons
    'M4 28 L14 26 L30 24 L50 22 L70 20 L90 18 L104 18 L112 20 L114 22 L108 24 L90 26 L70 28 L50 30 L30 32 L14 32 Z',
    // Wings
    'M46 26 L30 40 L26 42 L36 40 L62 28',
    // Tail
    'M100 18 L104 8 L110 8 L108 18',
    // ROTODOME — distinctive disc on struts
    'M52 22 L50 16 L48 12 L44 10 L40 10 L38 8 L80 8 L78 10 L74 10 L72 12 L70 16 L68 22',
    'M38 8 L36 6 L82 6 L80 8',
  ],
  // ===== AIR — RPA =====
  'MQ-9 Reaper': [
    // Slender fuselage, long straight wings, V-tail, pusher prop
    'M8 26 L20 24 L40 22 L60 22 L80 22 L100 22 L108 24 L110 26 L108 28 L100 28 L80 28 L60 28 L40 28 L20 28 Z',
    // Long straight wings
    'M40 24 L18 16 L14 14 L22 16 L44 24',
    'M40 28 L18 36 L14 38 L22 36 L44 28',
    // V-tail
    'M100 22 L106 14 L110 14 L108 22',
    'M100 28 L106 36 L110 36 L108 28',
    // Sensor ball
    'M12 26 Q8 24 12 22 Q16 24 12 26',
  ],
  'RQ-4 Global Hawk': [
    // Bulbous nose (sensor dome), very long straight wings, V-tail, jet engine hump
    'M4 24 Q4 20 10 20 L20 22 L40 22 L60 22 L80 20 L90 18 L100 20 L108 22 L110 24 L108 26 L100 28 L80 28 L60 28 L40 28 L20 28 L10 28 Q4 28 4 24 Z',
    // Very long wings
    'M36 24 L8 12 L4 10 L12 12 L40 24',
    'M36 28 L8 40 L4 42 L12 40 L40 28',
    // V-tail
    'M100 20 L106 12 L110 12 L108 20',
    // Engine hump
    'M78 20 L82 16 L92 16 L94 18',
  ],
  // ===== AIR — AIRLIFT =====
  'C-5M Galaxy': [
    // Massive high-wing transport, T-tail, 4 engines, upswept rear
    'M4 28 L10 26 L20 24 L40 22 L60 20 L80 20 L100 22 L108 26 L112 30 L108 32 L90 32 L60 32 L40 32 L20 32 L10 30 Z',
    // High wings
    'M34 22 L14 14 L10 12 L18 14 L42 22',
    'M60 20 L80 14 L84 12 L80 14 L64 20',
    // T-tail
    'M104 24 L108 14 L112 14 L110 24',
    'M106 14 L100 10 L114 10 L112 14',
    // Engine pods (4)
    'M22 18 L20 22 L26 22 L24 18',
    'M32 16 L30 20 L36 20 L34 16',
    'M64 16 L62 20 L68 20 L66 16',
    'M74 14 L72 18 L78 18 L76 14',
  ],
  'C-17 Globemaster III': [
    // High-wing transport, T-tail, 4 engines, shorter than C-5
    'M6 28 L14 26 L28 24 L46 22 L66 20 L86 20 L100 22 L108 26 L112 30 L108 32 L86 32 L66 32 L46 32 L28 32 L14 30 Z',
    // High wings
    'M38 22 L20 14 L16 12 L24 14 L44 22',
    'M62 20 L80 14 L84 12 L80 14 L66 20',
    // T-tail
    'M104 24 L108 14 L112 14 L110 24',
    'M106 14 L100 10 L114 10 L112 14',
    // Engine pods (4)
    'M26 18 L24 22 L30 22 L28 18',
    'M36 16 L34 20 L40 20 L38 16',
    'M66 16 L64 20 L70 20 L68 16',
    'M76 14 L74 18 L80 18 L78 14',
  ],
  // ===== AIR — TANKER =====
  'KC-135 Stratotanker': [
    // 707-based, low wing, conventional tail, 4 engines, boom
    'M4 26 L14 24 L30 22 L50 20 L70 18 L90 18 L104 18 L112 20 L114 22 L108 24 L90 26 L70 28 L50 30 L30 32 L14 30 Z',
    // Low wings
    'M36 28 L18 36 L14 38 L22 36 L42 28',
    'M64 26 L82 34 L86 36 L82 34 L68 26',
    // Tail
    'M100 18 L104 8 L110 8 L108 18',
    // Engine pods (4)
    'M24 32 L22 36 L28 36 L26 32',
    'M34 30 L32 34 L38 34 L36 30',
    'M68 28 L66 32 L72 32 L70 28',
    'M76 28 L74 32 L80 32 L78 28',
    // Refueling boom
    'M6 28 L2 34',
  ],
  // ===== AIR — CAS =====
  'A-10C Warthog': [
    // Twin engine pods high-mounted, straight wing, twin tails, GAU-8 nose
    'M2 26 L10 24 L24 22 L40 20 L60 20 L80 20 L96 20 L104 22 L106 24 L104 26 L96 28 L80 28 L60 28 L40 28 L24 28 L10 28 Z',
    // Straight wings
    'M36 22 L18 14 L14 12 L22 14 L42 22',
    'M36 28 L18 36 L14 38 L22 36 L42 28',
    // Twin tails
    'M92 20 L90 10 L94 10 L94 20',
    'M100 20 L98 10 L102 10 L102 20',
    // Engine pods (high-mounted)
    'M74 20 L72 16 L68 14 L68 12 L78 12 L78 14 L76 16 L76 20',
    'M84 20 L82 16 L78 14 L78 12 L88 12 L88 14 L86 16 L86 20',
    // GAU-8 cannon nose
    'M2 26 L-2 26 L-2 24 L2 24',
  ],
  // ===== AIR — SAP =====
  'TR-3B Black Manta': [
    // Triangular craft — equilateral triangle, center glow, three vertices
    'M60 4 L108 40 L12 40 Z',
    // Inner triangle (reactor glow outline)
    'M60 14 L92 36 L28 36 Z',
    // Center reactor core
    'M60 20 L72 32 L48 32 Z',
    // Three vertex lights
    'M58 6 L62 6 L62 10 L58 10 Z',
    'M106 38 L110 38 L110 42 L106 42 Z',
    'M10 38 L14 38 L14 42 L10 42 Z',
  ],
  // ===== SEA =====
  'F/A-18E/F Super Hornet': [
    // Twin engine, twin canted tails, LEX, mid-wing
    'M6 26 L18 24 L28 20 L42 18 L60 16 L80 14 L95 12 L108 14 L112 16 L110 18 L100 20 L80 22 L60 24 L40 26 L28 28 L18 28 Z',
    // Wings with LEX
    'M28 22 L32 20 L38 24 L24 38 L20 40 L28 38 L52 26',
    // Twin tails (canted)
    'M94 14 L96 4 L100 4 L100 14',
    'M100 14 L104 4 L108 4 L106 14',
  ],
  'E-2D Advanced Hawkeye': [
    // Turboprop, high wing, rotodome, 4 tail fins
    'M6 26 L16 24 L30 22 L50 20 L70 20 L90 20 L104 20 L110 22 L112 24 L108 26 L90 28 L70 28 L50 30 L30 30 L16 28 Z',
    // High wings
    'M38 22 L22 14 L18 12 L26 14 L44 22',
    'M62 20 L78 14 L82 12 L78 14 L66 20',
    // Rotodome
    'M44 20 L42 14 L40 10 L36 8 L80 8 L78 10 L76 14 L74 20',
    'M36 8 L34 6 L82 6 L80 8',
    // 4 tail fins
    'M100 20 L98 12 L102 12 L102 20',
    'M104 20 L102 12 L106 12 L106 20',
    'M100 28 L98 34 L102 34 L102 28',
    'M104 28 L102 34 L106 34 L106 28',
  ],
  'P-8A Poseidon': [
    // 737-based maritime patrol, low wing, conventional tail, 2 engines
    'M4 26 L14 24 L30 22 L50 20 L70 18 L90 18 L104 18 L112 20 L114 22 L108 24 L90 26 L70 28 L50 30 L30 32 L14 30 Z',
    // Low wings
    'M36 28 L20 36 L16 38 L24 36 L44 28',
    'M64 26 L80 34 L84 36 L80 34 L68 26',
    // Tail
    'M100 18 L104 8 L110 8 L108 18',
    // Engine pods (2)
    'M28 30 L26 34 L32 34 L30 30',
    'M72 28 L70 32 L76 32 L74 28',
    // MAD boom (tail stinger)
    'M112 22 L118 22',
  ],
  'MQ-25A Stingray': [
    // Stealthy UAS, blended wing body, no vertical tail, single engine
    'M10 26 L22 24 L36 20 L50 18 L70 18 L90 20 L104 22 L110 24 L112 26 L110 28 L104 28 L90 28 L70 28 L50 28 L36 28 L22 28 Z',
    // Blended wings
    'M36 22 L20 14 L16 12 L24 14 L42 22',
    'M36 28 L20 36 L16 38 L24 36 L42 28',
    // Intake
    'M68 18 L66 16 L74 16 L72 18',
  ],
  // ===== SPACE =====
  'GPS III Constellation': [
    // Satellite with solar panels
    'M46 18 L54 18 L56 20 L56 28 L54 30 L46 30 L44 28 L44 20 Z',
    // Solar panel left
    'M44 20 L10 16 L10 32 L44 28',
    // Solar panel right
    'M56 20 L90 16 L90 32 L56 28',
    // Panel lines left
    'M20 18 L20 30', 'M30 17 L30 31',
    // Panel lines right
    'M70 18 L70 30', 'M80 17 L80 31',
    // Antenna
    'M50 18 L50 10 L48 8 L52 8 L50 10',
  ],
  'SBIRS/OPIR': [
    // Early warning satellite — distinctive sensor dome + solar panels
    'M44 16 L56 16 L58 18 L58 30 L56 32 L44 32 L42 30 L42 18 Z',
    // Solar panels
    'M42 20 L12 16 L12 32 L42 28',
    'M58 20 L88 16 L88 32 L58 28',
    // Sensor dome
    'M46 16 L48 10 Q50 6 52 10 L54 16',
    // Panel lines
    'M22 18 L22 30', 'M32 17 L32 31',
    'M68 18 L68 30', 'M78 17 L78 31',
  ],
  'Space Fence': [
    // Ground-based radar array — antenna dishes
    'M20 38 L100 38',
    // Array panels
    'M28 38 L24 14 L36 14 L32 38',
    'M48 38 L44 10 L56 10 L52 38',
    'M68 38 L64 14 L76 14 L72 38',
    'M88 38 L84 18 L96 18 L92 38',
    // Signal waves
    'M40 8 Q50 4 60 8', 'M36 6 Q50 0 64 6',
  ],
  'WGS SATCOM': [
    // Communications satellite — large dish + solar panels
    'M46 18 L54 18 L56 20 L56 28 L54 30 L46 30 L44 28 L44 20 Z',
    // Solar panels
    'M44 20 L14 16 L14 32 L44 28',
    'M56 20 L86 16 L86 32 L56 28',
    // Large comm dish
    'M46 18 L42 10 Q50 4 58 10 L54 18',
    // Panel lines
    'M24 18 L24 30', 'M34 17 L34 31',
    'M66 18 L66 30', 'M76 17 L76 31',
  ],
  // ===== CYBER =====
  'Cyber Mission Force': [
    // Shield with circuit pattern
    'M40 6 L80 6 L84 10 L84 30 Q62 44 60 44 Q58 44 36 30 L36 10 Z',
    // Circuit lines
    'M48 14 L72 14', 'M48 20 L72 20', 'M48 26 L66 26',
    'M54 14 L54 26', 'M66 14 L66 26',
    // Terminal cursor
    'M50 32 L52 32 L52 36 L50 36 Z',
  ],
  'National Mission Teams': [
    // Shield with star
    'M40 6 L80 6 L84 10 L84 30 Q62 44 60 44 Q58 44 36 30 L36 10 Z',
    // Star
    'M60 12 L63 20 L72 20 L65 26 L68 34 L60 28 L52 34 L55 26 L48 20 L57 20 Z',
  ],
  'Cyber Protection Teams': [
    // Shield with lock
    'M40 6 L80 6 L84 10 L84 30 Q62 44 60 44 Q58 44 36 30 L36 10 Z',
    // Lock body
    'M52 22 L68 22 L68 34 L52 34 Z',
    // Lock shackle
    'M54 22 L54 16 Q54 12 60 12 Q66 12 66 16 L66 22',
  ],
  'Combat Mission Teams': [
    // Shield with lightning bolt
    'M40 6 L80 6 L84 10 L84 30 Q62 44 60 44 Q58 44 36 30 L36 10 Z',
    // Lightning bolt
    'M64 10 L54 24 L60 24 L52 38 L70 22 L62 22 L70 10 Z',
  ],
  // ===== GROUND =====
  'USAF Security Forces': [
    // Helmet + shield badge
    'M44 10 Q44 4 60 4 Q76 4 76 10 L76 22 L74 26 L68 28 L60 30 L52 28 L46 26 L44 22 Z',
    // Visor
    'M48 16 L72 16 L72 20 L48 20 Z',
    // Shield on chest
    'M54 32 L66 32 L68 34 L68 42 L60 46 L52 42 L52 34 Z',
  ],
  'TACP Specialists': [
    // Helmet with radio antenna + binoculars
    'M44 10 Q44 4 60 4 Q76 4 76 10 L76 22 L74 26 L68 28 L60 30 L52 28 L46 26 L44 22 Z',
    // NVG mount
    'M50 14 L46 12 L42 16 L42 22 L46 22 L46 18 L50 16',
    'M70 14 L74 12 L78 16 L78 22 L74 22 L74 18 L70 16',
    // Radio antenna
    'M74 8 L80 2 L82 2 L76 8',
  ],
  'Combat Controllers': [
    // Beret with flash + radio
    'M42 18 Q42 8 60 6 Q78 8 78 18 L76 22 L44 22 Z',
    // Beret flash
    'M56 10 L64 10 L64 16 L56 16 Z',
    // Headset
    'M42 18 L38 22 L38 28 L42 28 L42 22',
    'M78 18 L82 22 L82 28 L78 28 L78 22',
    // Radio pack
    'M52 30 L68 30 L68 42 L52 42 Z',
    'M56 34 L64 34', 'M56 38 L64 38',
  ],
  'DAGRE/Base Defense': [
    // Tactical helmet + rifle silhouette
    'M44 8 Q44 2 60 2 Q76 2 76 8 L76 18 L74 22 L68 24 L60 26 L52 24 L46 22 L44 18 Z',
    // NVG
    'M52 12 L48 10 L44 14 L44 18 L48 18 L48 14 L52 12',
    // Rifle
    'M20 32 L100 32 L102 30 L104 32 L104 34 L100 34 L20 34 Z',
    'M24 28 L28 28 L28 32',
    'M40 30 L44 26 L46 26 L42 32',
  ],
  // ===== FALLBACK =====
  _default: [
    // Generic aircraft
    'M10 26 L30 22 L50 20 L70 18 L90 18 L104 18 L110 20 L112 24 L108 26 L90 28 L70 30 L50 30 L30 28 Z',
    'M40 26 L24 36 L20 38 L28 36 L48 26',
    'M98 18 L102 10 L108 10 L106 18',
  ],
}

/**
 * Quick lookup: does this asset type have a custom silhouette?
 */
export function hasSilhouette(assetType: string): boolean {
  return assetType in SILHOUETTE_PATHS
}
